// Custom-element lifecycle also handles Shopify section replacement without global listeners.
if (!customElements.get('os-size-guide')) {
  customElements.define('os-size-guide', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.trigger = this.closest('product-details')?.querySelector('[data-size-guide-open]');
      if (!this.dialog || !this.trigger) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.mobile = window.matchMedia('(max-width: 767px)');
      this.tabs = Array.from(this.querySelectorAll('[data-size-guide-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-size-guide-panel]'));
      this.activeTab = this.dataset.defaultTab === 'bottoms' ? 'bottoms' : 'tops';
      this.trigger.addEventListener('click', () => this.open(), options);
      this.querySelector('[data-size-guide-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => this.restore(), options);
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.dialog.close();
      }, options);
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = Array.from(this.dialog.querySelectorAll('button, a[href], [tabindex="0"]'))
          .filter((element) => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }, options);
      // Require both ends of a pointer gesture outside, avoiding accidental drag-to-close.
      this.dialog.addEventListener('pointerdown', (event) => {
        this.backdropStart = this.isOutside(event);
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (!this.mobile.matches && this.backdropStart && this.isOutside(event)) this.dialog.close();
        this.backdropStart = false;
      }, options);
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.selectTab(tab.dataset.sizeGuideTab), options);
        tab.addEventListener('keydown', (event) => {
          const index = this.tabs.indexOf(tab);
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index + this.tabs.length - 1) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.selectTab(this.tabs[next].dataset.sizeGuideTab);
          this.tabs[next].focus();
        }, options);
      });
      this.mobile.addEventListener('change', () => this.selectTab(this.activeTab), options);
      this.selectTab(this.activeTab);
    }

    isOutside(event) {
      const bounds = this.dialog.getBoundingClientRect();
      return event.target === this.dialog && (
        event.clientX < bounds.left || event.clientX > bounds.right ||
        event.clientY < bounds.top || event.clientY > bounds.bottom
      );
    }

    selectTab(name) {
      this.activeTab = name;
      this.tabs.forEach((tab) => {
        const selected = tab.dataset.sizeGuideTab === name;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        panel.hidden = this.mobile.matches && panel.dataset.sizeGuidePanel !== name;
        if (this.mobile.matches) {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', panel.dataset.tabLabel);
          panel.tabIndex = 0;
        } else {
          panel.removeAttribute('role');
          panel.removeAttribute('aria-labelledby');
          panel.removeAttribute('tabindex');
        }
      });
    }

    open() {
      if (this.dialog.open) return;
      this.scrollPosition = { x: window.scrollX, y: window.scrollY };
      this.dialog.showModal();
      // A fixed body also prevents background touch scrolling on iOS.
      this.bodyStyles = {};
      ['position', 'top', 'left', 'width'].forEach((property) => {
        this.bodyStyles[property] = {
          value: document.body.style.getPropertyValue(property),
          priority: document.body.style.getPropertyPriority(property),
        };
      });
      document.body.style.position = 'fixed';
      document.body.style.top = `${-this.scrollPosition.y}px`;
      document.body.style.left = `${-this.scrollPosition.x}px`;
      document.body.style.width = '100%';
      this.querySelector('[data-size-guide-close]').focus({ preventScroll: true });
    }

    restore() {
      if (!this.bodyStyles) return;
      Object.entries(this.bodyStyles).forEach(([property, saved]) => {
        document.body.style.setProperty(property, saved.value, saved.priority);
      });
      this.bodyStyles = null;
      // Explicitly bypass any theme smooth scrolling when restoring the page.
      window.scrollTo({ left: this.scrollPosition.x, top: this.scrollPosition.y, behavior: 'instant' });
      if (this.trigger.isConnected) this.trigger.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      this.controller?.abort();
      if (this.dialog?.open) this.dialog.close();
      this.restore();
    }
  });
}
